import Close from "@mui/icons-material/Close";
import { Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from "@mui/material";

export type RightDrawerShellWidth = { xs?: string; sm?: number; md?: number };

const DEFAULT_WIDTH: RightDrawerShellWidth = { xs: "90%", sm: 576, md: 672 };

export interface RightDrawerShellProps {
  open: boolean;
  onClose: () => void;
  /** Optional title shown on the left of the header row */
  title?: React.ReactNode;
  width?: RightDrawerShellWidth;
  /** Icon buttons rendered before the close control (e.g. download, edit) */
  headerActions?: React.ReactNode;
  /** Fixed content between header and scrollable body (e.g. file preview) */
  preview?: React.ReactNode;
  /** Pinned below the scrollable region (e.g. Save / Cancel) */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** When false, drawer paper is empty (e.g. while parent data is loading) */
  showContent?: boolean;
  /** Default `true` — suppresses MUI's body scroll lock to avoid the
   *  scrollbar-width jitter on open/close. */
  disableScrollLock?: boolean;
  /**
   * Render a transparent (rather than dimmed) backdrop so the drawer feels like
   * an inspector panel. The backdrop still captures outside clicks and fires
   * `onClose`, so click-outside-to-close works either way.
   */
  hideBackdrop?: boolean;
}

/**
 * Shared right-anchored MUI Drawer layout: header row (title + actions + close),
 * optional preview, scrollable body, optional footer.
 */
export function RightDrawerShell({
  open,
  onClose,
  title,
  width = DEFAULT_WIDTH,
  headerActions,
  preview,
  footer,
  children,
  showContent = true,
  disableScrollLock = true,
  hideBackdrop = false,
}: RightDrawerShellProps) {
  const w = { ...DEFAULT_WIDTH, ...width };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      disableScrollLock={disableScrollLock}
      slotProps={hideBackdrop ? { backdrop: { invisible: true } } : undefined}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: w.xs, sm: w.sm, md: w.md },
          boxSizing: "border-box",
        },
      }}
    >
      {showContent ? (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
            }}
          >
            {title != null && title !== "" ? (
              <Typography component="div" variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
                {title}
              </Typography>
            ) : (
              <Box sx={{ flex: 1, mr: 1 }} />
            )}
            <Stack direction="row" spacing={0.5} alignItems="center">
              {headerActions}
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose} aria-label="Close drawer">
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Divider />

          {preview}
          {preview ? <Divider /> : null}

          <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 0 }}>{children}</Box>

          {footer ? (
            <>
              <Divider />
              <Box sx={{ flexShrink: 0, p: 2, bgcolor: "background.paper" }}>{footer}</Box>
            </>
          ) : null}
        </Box>
      ) : null}
    </Drawer>
  );
}
